<template>
  <div
    v-if="!header.hideHeader"
    class="ns-header"
    :class="{ 'show-search-results': showSearchResults }"
  >
    <header
      :id="header.id"
      class="header-container"
      :class="{
        rtl: dir === 'rtl',
        'hide-header-center-top': header.hideTopCenter
      }"
    >
      <!-- ------------------ -->
      <!-- UTILITY BAR MOBILE -->
      <!-- ------------------ -->
      <div
        v-if="
          !desktop &&
            !header.hideUtilityBar &&
            (header.marketingMessage.info || header.marketingMessage.link)
        "
        v-show="showMobileMarketingMessage"
        class="utility-bar-area"
      >
        <div class="header-max-width utility-bar-body">
          <!-- TOP CENTER -->
          <!-- ---------- -->
          <div v-if="!header.hideTopCenter" class="utility-bar-item top-center">
            <slot name="top-center">
              <NsHeaderMarketingMessage
                v-if="!header.hideMarketingMessage"
                :message="header.marketingMessage"
                @close="closeMarketingMessage"
              />
            </slot>
          </div>
        </div>
      </div>

      <!-- ------------------- -->
      <!-- UTILITY BAR DESKTOP -->
      <!-- ------------------- -->
      <div v-if="desktop && !header.hideUtilityBar" class="utility-bar-area">
        <div
          class="header-max-width utility-bar-body"
          :class="[utilityBarCenterClass]"
        >
          <!-- TOP LEFT -->
          <!-- -------- -->
          <div v-if="!header.hideTopLeft" class="utility-bar-item top-left">
            <slot name="top-left">
              <NsHeaderLegalLinks
                v-if="
                  (!header.hideLegalLinks && header.legalLinks.length > 0) ||
                    header.accessibilityLink.showAccessibilityLink === true
                "
                :links="header.legalLinks"
                :accessibility-link="header.accessibilityLink"
                :hide-legal-links="header.hideLegalLinks"
              />
            </slot>
          </div>

          <!-- TOP CENTER -->
          <!-- ---------- -->
          <div v-if="!header.hideTopCenter" class="utility-bar-item top-center">
            <slot name="top-center">
              <NsHeaderMarketingMessage
                v-if="!header.hideMarketingMessage"
                :message="header.marketingMessage"
                @close="closeMarketingMessage"
              />
            </slot>
          </div>

          <!-- TOP RIGHT -->
          <!-- --------- -->
          <div v-if="!header.hideTopRight" class="utility-bar-item top-right">
            <slot name="top-right">
              <!-- REGION SELECTOR -->
              <NsHeaderRegionSelector
                v-if="!header.hideRegionSelector"
                :href-override="header.homeUrl"
                :region="header.region"
                :region-selector-path="header.regionSelectorPath"
                :regions-allowed="header.regionsAllowed"
                :translations="header.translations"
              />

              <!-- SIGN UP -->
              <slot name="profile">
                <NsHeaderProfileDropdown
                  v-if="!header.hideProfile"
                  class="ns-profile"
                  :hide-sign-up="header.hideSignUp"
                  :profile-menu="header.profileMenu"
                  :sign-out-url="header.signOutUrl"
                  :sign-up-url="header.signUpUrl"
                  :translations="header.profileTranslations"
                />
              </slot>
            </slot>
          </div>
        </div>
      </div>

      <!-- ------------- -->
      <!-- HEADER BODY -->
      <!-- ------------- -->
      <div class="header-body">
        <div class="header-layout header-max-width">
          <slot name="header">
            <!-- HEADER LEFT -->
            <!-- ----------- -->
            <div v-if="!header.hideBottomLeft" class="header-left">
              <!-- MENU BUTTON -->
              <slot name="left">
                <div class="header-left-one">
                  <slot name="left-one">
                    <div
                      v-if="
                        !header.hideMenu &&
                          !(header.hideUtilityBar && header.hideTopNav)
                      "
                      class="menu-button"
                      :class="`open-${isMenuOpen}`"
                      @click="toggleMenu"
                    >
                      <NsIcon icon-name="icon-hamburger" />
                    </div>
                  </slot>
                </div>

                <!-- LOGO -->
                <div v-if="!hideLogo" class="header-left-two">
                  <slot name="left-two">
                    <NsLogo
                      :is-mysite="
                        header.distributorImage ||
                        useMysiteHeader ||
                        isHeaderDataMysite
                          ? true
                          : false
                      "
                      :mysite-image-url="header.distributorImage"
                      :link-url="disableLogoLink ? '' : header.homeUrl || '/'"
                      :alt-text="
                        header.distributorImage ||
                        useMysiteHeader ||
                        isHeaderDataMysite
                          ? 'Nu Skin Logo on the Mysite Header'
                          : 'Nu Skin Logo on the Header'
                      "
                      :is-market-logo-override="
                        marketOverrideCondition &&
                        !(
                          header.distributorImage ||
                          useMysiteHeader ||
                          isHeaderDataMysite
                        )
                          ? true
                          : false
                      "
                      :market-logo-desktop-url="
                        marketOverrideCondition
                          ? 'https://media.nuskin.com/m/67cf60cef6134c90/original/japan_30_anniversary_desktop.png'
                          : ''
                      "
                      :market-logo-mobile-url="
                        marketOverrideCondition
                          ? 'https://media.nuskin.com/m/62008b0b85bdedc5/original/japan_30_anniversary_mobile.png'
                          : ''
                      "
                      :is-word-mark="
                        !(
                          marketOverrideCondition ||
                          header.distributorImage ||
                          useMysiteHeader ||
                          isHeaderDataMysite
                        )
                          ? true
                          : false
                      "
                    />
                  </slot>
                </div>
              </slot>
            </div>

            <!-- HEADER CENTER-->
            <!-- ------------ -->
            <div v-if="!header.hideBottomCenter" class="header-center">
              <slot name="center">
                <!-- CENTER TOP (SEARCHBAR) -->
                <div class="header-center-top">
                  <slot name="center-top">
                    <NsSearchbar
                      v-if="!header.hideSearchBar"
                      ref="searchbar"
                      class="ns-searchbar"
                      :disable-predictive-search="
                        header.disablePredictiveSearch
                      "
                      :translations="header.searchbarTranslations"
                      :popular-search-array="header.popularSearchArray"
                      :hide-see-more-results="header.hideSeeMoreResults"
                      :using-tooltips="header.showToolTip"
                      :order-type-in-predictive="header.orderTypeInPredictive"
                      :search-options="header.searchOptions"
                      new-header
                      @close-search-results="handleCloseSearchResults"
                    />
                  </slot>
                </div>

                <!-- CENTER BOTTOM (MAIN NAVIGATION) -->
                <div v-if="desktop" class="header-center-bottom">
                  <slot name="center-bottom">
                    <div v-if="!header.hideTopNav">
                      <NsTopnav
                        :local-navigation="header.navigation"
                        :use-top-level-nav="useTopLevelNav"
                      />
                    </div>
                  </slot>
                </div>
              </slot>
            </div>

            <!-- HEADER RIGHT -->
            <!-- ------------ -->
            <div v-if="!header.hideBottomRight" class="header-right">
              <slot name="right">
                <!-- LINKS -->
                <div class="header-right-one">
                  <slot name="right-one">
                    <a
                      v-if="
                        !header.hideJoinUs &&
                          header.joinUs.label &&
                          header.joinUs.link
                      "
                      class="join-link"
                      :href="header.joinUs.link"
                    >
                      {{ header.joinUs.label }}
                    </a>
                    <NsHeaderLoyaltyRewards
                      v-if="!header.hideLoyalty"
                      :translations="header.translations"
                    />
                  </slot>
                </div>

                <!-- CART -->
                <div class="header-right-two">
                  <slot name="right-two">
                    <NsMiniCart
                      v-if="!header.hideCart"
                      class="ns-mini-cart"
                      :shopping-bag-text="header.cartText"
                      :show-shopping-bag-text="header.showCartText"
                    />
                  </slot>
                </div>
              </slot>
            </div>
          </slot>
        </div>
      </div>

      <!-- ------------- -->
      <!-- MOBILE DRAWER -->
      <!-- ------------- -->
      <NsDrawer
        v-if="!desktop && !(header.hideUtilityBar && header.hideTopNav)"
        :open="mobileDrawerOpen"
        width="300"
        @close="mobileDrawerOpen = false"
      >
        <NsHeaderDrawer
          :navigation="header.navigation"
          :region="header.region"
          :href-override="header.homeUrl"
          :regions-allowed="header.regionsAllowed"
          :region-selector-path="header.regionSelectorPath"
          :signup-href="header.signUpUrl"
          :translations="header.translations"
          :join-us="header.joinUs"
          :legal-links="header.legalLinks"
          :hide-top-nav="header.hideTopNav"
          :hide-legal-links="header.hideLegalLinks || header.hideUtilityBar"
          :hide-loyalty="header.hideLoyalty"
          :hide-region-selector="
            header.hideRegionSelector || header.hideUtilityBar
          "
          :hide-join-us="header.hideJoinUs"
          hide-footer-nav
          hide-footer-signup
          @close-drawer="mobileDrawerOpen = false"
        >
          <template #header>
            <NsHeaderProfileDropdownMobile
              v-if="!header.hideProfile && !header.hideUtilityBar && !desktop"
              :hide-sign-up="header.hideSignUp"
              :profile-menu="header.profileMenu"
              :sign-out-url="header.signOutUrl"
              :sign-up-url="header.signUpUrl"
              :translations="header.profileTranslations"
              @close-drawer="mobileDrawerOpen = false"
            />
          </template>
        </NsHeaderDrawer>
      </NsDrawer>
    </header>

    <div ref="searchResults" class="header-search-results">
      <slot name="search-results">
        <NsSearchResults
          :show-search-results="showSearchResults"
          :translations="header.searchResultsTranslations"
          @scroll-to-top="scrollToTopOfSearchResults"
          @back-button-clicked="handleBackButtonClicked"
        ></NsSearchResults>
      </slot>
    </div>
  </div>
</template>

<script>
import {
  events,
  DirUtil,
  PersonalOfferStorageService,
  StoreFrontSponsorStorageService,
  ShoppingContext,
  UrlService
} from "@nuskin/ns-util";
import { MarketHeaderService } from "@nuskin/ns-product";
import { NsIcon } from "@nuskin/design-components";
import { MySiteRestService } from "@nuskin/my-site-api";
import { isNullOrEmpty, fromJsonString } from "@nuskin/ns-common-lib";

import NsHeaderLegalLinks from "./NsHeaderLegalLinks.vue";
import NsHeaderMarketingMessage from "./NsHeaderMarketingMessage.vue";
import NsHeaderRegionSelector from "./NsHeaderRegionSelector.vue";
import NsHeaderLoyaltyRewards from "./NsHeaderLoyaltyRewards.vue";
import NsHeaderProfileDropdown from "./NsHeaderProfileDropdown.vue";
import NsHeaderProfileDropdownMobile from "./NsHeaderProfileDropdownMobile.vue";
import NsLogo from "./NsLogo.vue";
import NsMiniCart from "./NsMiniCart.vue";
import NsSearchbar from "./NsSearchbar.vue";
import NsTopnav from "./NsTopnav.vue";
import NsDrawer from "./NsDrawer.vue";
import NsHeaderDrawer from "./NsHeaderDrawer.vue";
import NsSearchResults from "./NsSearchResults.vue";
import vueEventBus from "./EventBus";

const DESKTOP_MAX_MARKETING_LENGTH = 100;

export default {
  name: "NsHeaderAem",
  components: {
    NsIcon,
    NsHeaderLegalLinks,
    NsHeaderMarketingMessage,
    NsHeaderRegionSelector,
    NsHeaderLoyaltyRewards,
    NsHeaderProfileDropdown,
    NsHeaderProfileDropdownMobile,
    NsLogo,
    NsMiniCart,
    NsSearchbar,
    NsTopnav,
    NsDrawer,
    NsHeaderDrawer,
    NsSearchResults
  },
  // props will override the data from marketHeaderService
  props: {
    /**
     * Allows you to set the image for the header (distributor profile image).
     * You must also set either hrefOverride (can be done in the service) or logoUrl for this to show
     */
    distributorImage: {
      type: String,
      default: ""
    },
    /**
     * Hides the cart
     */
    hideCart: Boolean,
    /**
     * Hides the entire header
     */
    hideHeader: Boolean,
    /**
     * Hides the top left section
     */
    hideTopLeft: Boolean,
    /**
     * Hides the bottom left section
     */
    hideBottomLeft: Boolean,
    /**
     * Hides the top right section
     */
    hideTopRight: Boolean,
    /**
     * Hides the bottom right section
     */
    hideBottomRight: Boolean,
    /**
     * Hides the bottom center section
     */
    hideBottomCenter: Boolean,
    /**
     * Hides the top center section
     */
    hideTopCenter: Boolean,
    /**
     * Hides profile component (Sign Up/Sign In)
     */
    hideProfile: Boolean,
    /**
     * Hides the search bar
     */
    hideSearchbar: Boolean,
    /**
     * Hides everything in the utility bar
     */
    hideUtilityBar: Boolean,
    /**
     * Hides the legal links
     */
    hideLegalLinks: Boolean,
    /**
     * Hides the marketing message
     */
    hideMarketingMessage: Boolean,
    /**
     * Hides the region selector
     */
    hideRegionSelector: Boolean,
    /**
     * Hides the menu
     */
    hideMenu: Boolean,
    /**
     * Hides the logo
     */
    hideLogo: Boolean,
    /**
     * Hides the top navigation
     */
    hideTopNav: Boolean,
    /**
     * Hides loyalty
     */
    hideLoyalty: Boolean,
    /**
     * Hides Join Us
     */
    hideJoinUs: Boolean,
    /**
     * Hides Sign Up
     */
    hideSignUp: Boolean,
    /**
     * If set, this will override the page redirect on the region selector page and the distributor logo (and logout redirect)
     */
    hrefOverride: {
      type: String,
      default: ""
    },
    /**
     * Allows you to set the id for the header component
     */
    id: {
      type: String,
      default: ""
    },
    /**
     * Boolean for if menu is open, toggles state of other elements accordingly
     */
    isMenuOpen: Boolean,
    /**
     * Translation and link for Join Us link
     */
    joinUs: {
      type: [Object, String],
      default: null
    },
    /**
     * Link for UsableNet accessibility
     */
    accessibilityLink: {
      type: [Object, String],
      default: null
    },
    /**
     * Array of links to display in the top utility bar: left
     * Objects in array require 'href' and 'name'
     */
    legalLinks: {
      type: [Array, String],
      default: null
    },
    /**
     * Set the URL that you are redirected to when clicking on the header/distributor logo
     */
    logoUrl: {
      type: String,
      default: ""
    },
    /**
     * Sets information in the top utility bar: center
     * Object requires 'info', 'href', and 'link'
     */
    marketingMessage: {
      type: [Object, String],
      default: null
    },
    /**
     * Used in conjunction with useHeaderService, setting this to true
     * will make the service return data for a My Site header (storefront, personal offer).
     */
    mysite: Boolean,
    /**
     * Used to force the component to ignore My Site header data.
     */
    noMysite: Boolean,
    /**
     * Navigation array for the mega dropdown
     */
    navigation: {
      type: [Array, String],
      default: null
    },
    /**
     * Array of menu items for the profile component
     */
    profileMenu: {
      type: [Array, String],
      default: null
    },
    /**
     * Translations for the profile
     */
    profileTranslations: {
      type: [Object, String],
      default: null
    },
    /**
     * Region
     */
    region: {
      type: [Object, String],
      default: null
    },
    /**
     * Allows you to set where the select other region redirects too
     */
    regionSelectorPath: {
      type: String,
      default: ""
    },
    /**
     * If set, this will only show regions according to what is in the array
     * [ "en_US", "es_US" ]
     */
    regionsAllowed: {
      type: String,
      default: ""
    },
    /**
     * Translations for the search results
     */
    searchResultsTranslations: {
      type: [Object, String],
      default: null
    },
    /**
     * Where to redirect after signing out
     */
    signOutUrl: {
      type: String,
      default: ""
    },
    /**
     * Toggles theme styles and functionality
     */
    theme: {
      type: String,
      default: "auto"
    },
    /**
     * Translations for the header
     * Options: 'selectOther'
     */
    translations: {
      type: [Object, String],
      default: null
    },
    /**
     * Don't use the AEM service to pull all the information needed to use the header
     */
    noHeaderService: Boolean,
    /**
     * Allows tabs for mega dropdown to be clickable to top-level category pages
     * Should only be used for dev purposes, as it is incompatible with touch
     * devices
     */
    useTopLevelNav: Boolean,
    /**
     * use a market override for the logo url source for <NsLogo> in the header
     *   reference the computed prop marketOverrideCondition
     *   if marketOverrideCondition resolves to true, assign the props `:market-logo-mobile-url`
     *   and `:market-logo-desktop-url` given to <NsLogo/> to be the market override logo source urls
     */
    logoUrlMarketOverride: {
      type: Boolean,
      default: false
    },
    /**
     * For disabling href link on logo
     */
    disableLogoLink: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      logoData: {},
      marketData: {},
      miniCartData: {},
      mobileDrawerOpen: false,
      profileData: {},
      searchResultsPageData: {},
      searchbarData: {},
      truncateTextOnSmallestBreakpoint: false,
      showMobileMarketingMessage: true,
      showSearchResults: false,
      hideLegalLinksEvent: false,
      hideSearchbarEvent: false,
      hideTopNavEvent: false,
      hideMenuEvent: false
    };
  },
  computed: {
    desktop() {
      return this.$mq === "desktop" || this.$mq === "desktopHD";
    },
    market() {
      return (
        this.$NsAppService.runConfig.market ||
        this.$NsAppService.runConfig.country ||
        ""
      );
    },
    language() {
      return this.$NsAppService.runConfig.language || "";
    },
    locale() {
      return this.$NsAppService.locale || "";
    },
    baseUrl() {
      return this.$NsAppService.runConfig.baseUrl || "";
    },
    isStorefront() {
      return !!this.$NsAppService.isStorefront;
    },
    useMysiteHeader() {
      return !this.noMysite && (this.isStorefront || this.mysite);
    },
    // noHeaderService must be true if you want to pass props manually instead
    header() {
      return {
        //The URL for a distributor profile image that is shown in place of the NS Logo
        distributorImage:
          this.distributorImage ||
          this.logoData.distributorImage ||
          this.marketData.distributorImage ||
          "",
        // marketData
        hideHeader:
          this.hideHeader || this.hidden || this.marketData.hideHeader || false,
        hideTopLeft:
          this.hideTopLeft || this.marketData.hideHeaderTopLeft || false,
        hideTopCenter:
          this.hideTopCenter || this.marketData.hideHeaderTopCenter || false,
        hideTopRight:
          this.hideTopRight || this.marketData.hideHeaderTopRight || false,
        hideBottomLeft:
          this.hideBottomLeft || this.marketData.hideHeaderBottomLeft || false,
        hideBottomCenter:
          this.hideBottomCenter ||
          this.marketData.hideHeaderBottomCenter ||
          false,
        hideBottomRight:
          this.hideBottomRight ||
          this.marketData.hideHeaderBottomRight ||
          false,
        hideProfile: this.hideProfile || this.marketData.hideMyAccount || false,
        hideSearchBar:
          this.hideSearchbar ||
          this.hideSearchbarEvent ||
          this.marketData.hideSearchBar ||
          false,
        hideUtilityBar:
          this.hideUtilityBar || this.marketData.hideUtilityBar || false,
        hideLegalLinks:
          this.hideLegalLinks ||
          this.hideLegalLinksEvent ||
          this.marketData.hideLegalLinks ||
          false,
        hideMarketingMessage:
          this.hideMarketingMessage ||
          this.marketData.hideMarketingMessage ||
          false,
        hideRegionSelector:
          this.hideRegionSelector ||
          this.marketData.hideRegionSelector ||
          false,
        hideMenu:
          this.hideMenu ||
          this.hideMenuEvent ||
          this.marketData.hideMenu ||
          false,
        hideLogo: this.hideLogo || this.marketData.hideLogo || false,
        hideTopNav:
          this.hideTopNav ||
          this.hideTopNavEvent ||
          this.marketData.hideTopNav ||
          false,
        hideLoyalty: this.hideLoyalty || this.marketData.hideLoyalty || false,
        hideJoinUs: this.hideJoinUs || this.marketData.hideJoinUs || false,
        hideCart: this.hideCart || this.marketData.hideShoppingBag || false,
        hideSignUp: this.hideSignUp || this.profileData.hideSignUp || false,
        headerType: this.marketData.headerType || "default",
        homeUrl:
          this.hrefOverride ||
          this.logoUrl ||
          this.logoData.linkUrl ||
          this.marketData.hrefOverride ||
          "",
        mySiteLabel: this.marketData.tdc_mySite || "",
        id: this.id || this.marketData.id || "ns-header",
        joinUs: this.j(this.joinUs) ||
          this.marketData.joinUs || {
            label: "Join",
            link: "/join.html"
          },
        accessibilityLink: this.j(this.accessibilityLink) ||
          this.marketData.accessibility || {
            showAccessibilityLink: false,
            accessibilityActivate: "Activate Accessibility",
            accessibilityDeactivate: "Turn Off Accessibility",
            accessibilityHref: "#",
            accessibilityStyle: "UsableNetAssistive",
            accessibilityOnclick: "return enableUsableNetAssistive()"
          },
        legalLinks: this.j(this.legalLinks) || this.marketData.legalLinks || [],
        marketingMessage: this.j(this.marketingMessage) ||
          this.marketData.marketingMessage || {
            info: "",
            href: "",
            link: ""
          },
        navigation:
          this.j(this.navigation) ||
          this.removeHTTPSPub(this.marketData.navigation) ||
          [],
        region: this.j(this.region) || this.marketData.region || {},
        regionSelectorPath:
          this.j(this.regionSelectorPath) ||
          this.marketData.regionSelectorPath ||
          "/content/nuskin.html#region-selection",
        translations: this.j(this.translations) ||
          this.marketData.translations || {
            selectOther: "Select Other",
            enjoyLoyalty: "Enjoy Rewards"
          },
        useNewHeader:
          this.useNewHeader || this.marketData.useNewHeader || false,
        useNewTopNav:
          this.useNewTopNav || this.marketData.useNewTopNav || false,
        // miniCartData
        cartText: this.miniCartData.shoppingBagText || "",
        showCartText: this.miniCartData.showShoppingBagText || false,
        // profile
        profileTranslations: (() => {
          const translations =
            this.j(this.profileTranslations) ||
            this.profileData.translations ||
            {};
          return {
            ...translations,
            enjoyLoyalty:
              this.marketData &&
              this.marketData.translations &&
              this.marketData.translations.enjoyLoyalty
                ? this.marketData.translations.enjoyLoyalty
                : "Enjoy Rewards",
            signIn: (translations.signIn && translations.signIn.name) || "",
            signOut: (translations.signOut && translations.signOut.name) || ""
          };
        })(),
        profileMenu:
          this.j(this.profileMenu) || this.profileData.profileMenu || [],
        newCustomerLabel:
          (
            this.j(this.profileTranslations) ||
            this.profileData.translations ||
            {}
          ).newCustomerLabel || "",
        signUpTodayLabel:
          (
            this.j(this.profileTranslations) ||
            this.profileData.translations ||
            {}
          ).signUpTodayLabel || "",
        signOutUrl: (() => {
          if (this.signOutUrl) {
            return this.signOutUrl;
          } else {
            const translations =
              this.j(this.profileTranslations) ||
              this.profileData.translations ||
              {};
            return (translations.signOut && translations.signOut.href) || "";
          }
        })(),
        signUpUrl: this.profileData.signUpUrl || "",
        // searchbar
        disablePredictiveSearch:
          this.searchbarData.disablePredictiveSearch || false,
        searchbarTranslations: this.searchbarData.translations || {},
        popularSearchArray: this.searchbarData.popularSearchArray || [],
        hideSeeMoreResults: this.searchbarData.hideSeeMoreResults || false,
        showToolTip: this.searchbarData.showToolTip || false,
        orderTypeInPredictive:
          this.searchbarData.orderTypeInPredictive || false,
        searchOptions: this.searchbarData.searchOptions || {},
        // search results
        searchResultsTranslations:
          this.j(this.searchResultsTranslations) ||
          this.searchResultsPageData.translations ||
          {},
        // storefront
        regionsAllowed:
          this.j(this.regionsAllowed) || this.marketData.regionsAllowed || [],
        showDistributorImage:
          this.distributorImage ||
          this.logoData.showDistributorImage ||
          this.marketData.showDistributorImage ||
          false
      };
    },
    isHeaderDataMysite() {
      return this.header.headerType === "nsHeaderStoreFront";
    },
    dir() {
      return DirUtil.getDir();
    },
    utilityBarCenterClass() {
      const { info = "", link = "" } = this.header.marketingMessage || {};

      if (this.header.marketingMessage) {
        return info.length + link.length > DESKTOP_MAX_MARKETING_LENGTH
          ? "unlock-center"
          : "";
      } else {
        return "";
      }
    },
    urlPrefix() {
      return (
        (this.market.toLowerCase() === "jp"
          ? "/content/markets/"
          : "/content/nuskin/") +
        this.language +
        "_" +
        this.market +
        "/"
      );
    },
    marketOverrideCondition() {
      // this market override is assigned as true for the japan market until October 1, 2023.
      // we can update this for a new market condition afterwards, or delete it.
      const now = new Date();
      const changeDate = new Date("October 1, 2023");
      let returnValue =
        (this.market.toLowerCase() === "jp" && now < changeDate) ||
        this.logoUrlMarketOverride;
      return returnValue;
    }
  },
  watch: {
    useMysiteHeader(useMysiteHeader) {
      if (
        useMysiteHeader !== this.isHeaderDataMysite ||
        !this.marketData.headerType
      ) {
        this.getPropsFromAjax();
      }
    }
  },
  async created() {
    this.updateForMySite();
  },
  async mounted() {
    this.addListeners();

    // navigating from personal offer to the home page in same window
    // ensures correct header is shown under these conditions
    if (
      this.header.headerType === "default" &&
      (this.useMysiteHeader || this.isStorefront || this.mysite) &&
      !window.location.href.includes("mysite")
    ) {
      this.getPropsFromAjax();
    }

    if (window.matchMedia("(max-width: 375px)").matches) {
      this.truncateTextOnSmallestBreakpoint = true;
    }
  },
  destroyed() {
    this.removeListeners();

    if (document.body.style.position === "fixed") {
      this.unlockBody();
    }
  },
  methods: {
    closeMarketingMessage() {
      this.showMobileMarketingMessage = false;
    },

    async getPropsFromAjax() {
      if (this.noHeaderService) {
        return;
      }

      let page = window.location.pathname.replace("#/", "");
      if (!page.includes(this.locale)) {
        page = "/content/";
        if (this.market.toLowerCase() === "jp") {
          page += "markets";
        } else {
          page += "nuskin";
        }
        page += `/${this.locale}/home.html`;
      } else {
        // Make sure JP is using content/markets path and not just /markets
        page =
          page.indexOf("content/markets/") > -1
            ? page
            : page.replace("/markets/", "/content/markets/");
      }

      const siteUrl = window.location.origin.startsWith("http://localhost")
        ? this.baseUrl || "https://test.nuskin.com"
        : window.location.origin;

      // remove mysite selector if not STOREFRONT context
      if (!this.isStorefront) {
        page = page.replace(".mysite.", ".");
      }
      const headerData = await MarketHeaderService.getMarketHeaderData(
        page,
        siteUrl,
        this.useMysiteHeader
      );

      if (isNullOrEmpty(this.logoData)) {
        this.logoData = headerData.logo || {};
      }

      this.marketData = headerData.market || {};
      this.miniCartData = headerData.miniCart || {};
      this.profileData = headerData.profile || {};
      this.searchResultsPageData = headerData.searchResultsPage || {};
      this.searchbarData = headerData.searchbar || {};
    },

    async updateForMySite() {
      if (this.isStorefront) {
        let storefrontSponsor = StoreFrontSponsorStorageService.getStoreFrontSponsor();
        let sponsorId = null;
        try {
          sponsorId = await MySiteRestService.getCurrentSponsorId();
        } catch {
          sponsorId = null;
        }
        // adjust for My Site
        if (sponsorId) {
          const subdomain = await MySiteRestService.getCurrentSubdomain();
          storefrontSponsor =
            StoreFrontSponsorStorageService.getStoreFrontSponsor() ||
            (await MySiteRestService.getSponsorData(sponsorId));

          if (
            ShoppingContext &&
            ShoppingContext.isContext(ShoppingContext.STOREFRONT)
          ) {
            this.logoData.linkUrl = `${UrlService.getAemLocaleUrl()}mysite/mysite-home.mysite.${subdomain}.html?storeId=${sponsorId}`;
          }

          this.logoData.showDistributorImage = !!storefrontSponsor;
          this.logoData.distributorImage = storefrontSponsor
            ? storefrontSponsor.siteLogo || storefrontSponsor.photoURL
            : `${UrlService.getAemLocaleUrl()}/content/dam/icons/logos/ns-logo.png`;

          const market = this.market.toUpperCase();
          try {
            if (
              storefrontSponsor &&
              storefrontSponsor.storefront &&
              storefrontSponsor.storefront[subdomain] &&
              storefrontSponsor.storefront[subdomain][market] &&
              storefrontSponsor.storefront[subdomain][market].lang &&
              storefrontSponsor.storefront[subdomain][market].lang[
                this.language
              ]
            ) {
              const hideSearchAndNav = !storefrontSponsor.storefront[subdomain][
                market
              ].lang[this.language].fullProductCatalog;
              if (hideSearchAndNav) {
                this.hideSearchbarEvent = true;
                this.hideTopNavEvent = true;
              }
            }
          } catch (ex) {
            console.error(ex);
          }
        }
        // adjust for Vera
        if (ShoppingContext.isContext(ShoppingContext.SKIN_CONSULT)) {
          this.logoData.linkUrl = `${this.baseUrl}/static/skin-consultation/#/${
            this.language
          }_${this.market.toUpperCase()}/recommendations`;
        }
        // adjust for Personal Offer
        if (PersonalOfferStorageService.isPersonalOffer()) {
          const personalOffer = PersonalOfferStorageService.getPersonalOffer();
          const picURL =
            personalOffer.profilePhoto || personalOffer.photoUrl
              ? personalOffer.profilePhoto || personalOffer.photoUrl
              : `${this.baseUrl}/content/dam/icons/logos/ns-logo.png`;
          this.logoData.linkUrl = personalOffer.landingPageURL;
          this.logoData.showDistributorImage = !!storefrontSponsor;
          this.logoData.distributorImage = storefrontSponsor
            ? storefrontSponsor.profilePhoto || storefrontSponsor.photoURL
            : picURL;

          this.hideSearchbarEvent = true;
          this.hideTopNavEvent = true;
          this.hideLegalLinksEvent = true;
        }
      } else {
        this.logoData = {};
      }
    },

    getOffSetHeight() {
      const domHeader = document.querySelector("#" + this.header.id);
      return domHeader ? domHeader.offsetHeight : 0;
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      window.addEventListener("resize", this.handleWindowResize);

      this.$watch(
        function() {
          return !this.$NsAppService.loading;
        },
        this.getPropsFromAjax,
        {
          immediate: true
        }
      );

      events.subscribe(
        events.global.RUN_CONFIG_CHANGED,
        this.$NsAppService.checkConfig
      );

      vueEventBus.$on("config-changed", this.handleConfigChanged);
      events.subscribe("updateHeaderLanguage", this.handleConfigChanged);

      events.subscribe(events.shop.SEARCH_EXECUTED, this.handleSearchExecuted);

      events.subscribe(
        events.header ? events.header.HIDE_SEARCHBAR : "headerHideSearchbar",
        this.handleHideHeaderSearchbar
      );

      events.subscribe(
        events.header ? events.header.HIDE_NAVIGATION : "headerHideNavigation",
        this.handleHideHeaderNavigation
      );

      events.subscribe(
        events.header
          ? events.header.SET_DISTRIBUTOR_IMAGE
          : "headerSetDistributorImage",
        this.handleSetDistributorImage
      );

      events.subscribe(
        events.header ? events.header.HIDE_MENU : "headerHideMenu",
        this.handleHideHeaderMenu
      );

      events.subscribe(
        events.header ? events.header.SET_LOGO_HREF : "headerSetLogoHref",
        this.handleSetHeaderLogoHref
      );

      events.subscribe(
        events.header
          ? events.header.SET_HREF_OVERRIDE
          : "headerSetHrefOverride",
        this.handleSetHeaderHrefOverride
      );
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      window.removeEventListener("resize", this.handleWindowResize);

      events.unsubscribe(
        events.global.RUN_CONFIG_CHANGED,
        this.$NsAppService.checkConfig
      );

      vueEventBus.$off();
      events.unsubscribe("updateHeaderLanguage", this.handleConfigChanged);

      events.unsubscribe(
        events.shop.SEARCH_EXECUTED,
        this.handleSearchExecuted
      );

      events.unsubscribe(
        events.header ? events.header.HIDE_SEARCHBAR : "headerHideSearchbar",
        this.handleHideHeaderSearchbar
      );

      events.unsubscribe(
        events.header ? events.header.HIDE_NAVIGATION : "headerHideNavigation",
        this.handleHideHeaderNavigation
      );

      events.unsubscribe(
        events.header
          ? events.header.SET_DISTRIBUTOR_IMAGE
          : "headerSetDistributorImage",
        this.handleSetDistributorImage
      );

      events.unsubscribe(
        events.header ? events.header.HIDE_MENU : "headerHideMenu",
        this.handleHideHeaderMenu
      );

      events.unsubscribe(
        events.header ? events.header.SET_LOGO_HREF : "headerSetLogoHref",
        this.handleSetHeaderLogoHref
      );

      events.unsubscribe(
        events.header
          ? events.header.SET_HREF_OVERRIDE
          : "headerSetHrefOverride",
        this.handleSetHeaderHrefOverride
      );
    },

    toggleMenu() {
      this.mobileDrawerOpen = !this.mobileDrawerOpen;
      events.publish("toggleDrawer");
    },

    scrollToTopOfSearchResults() {
      this.$refs.searchResults.scrollTop = 0;
    },

    lockBody() {
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = "fixed";
    },

    unlockBody() {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    },

    handleConfigChanged() {
      this.getPropsFromAjax();
      this.updateForMySite();
    },

    handleWindowResize() {
      setTimeout(() => {
        let offsetHeight = this.getOffSetHeight();
        document.documentElement.style.setProperty(
          "--header-height",
          `${offsetHeight}px`
        );
      }, 1);
    },

    handleSearchExecuted(searchObject) {
      this.showSearchResults =
        !!searchObject && !!searchObject.showSearchResults;

      if (this.showSearchResults) {
        this.lockBody();
      } else {
        this.unlockBody();
      }
    },

    handleBackButtonClicked() {
      this.$refs.searchbar.clearSearch();
    },

    handleCloseSearchResults() {
      this.showSearchResults = false;
      this.unlockBody();
    },

    handleHideHeaderSearchbar(hide) {
      this.hideSearchbarEvent = !!hide;
    },

    handleHideHeaderNavigation(hide) {
      this.hideTopNavEvent = !!hide;
    },

    handleSetDistributorImage(imageData) {
      if (imageData.url && imageData.url.length > 0) {
        if (imageData.hrefOverride) {
          if (
            imageData.aemSource &&
            imageData.hrefOverride.indexOf(this.urlPrefix) == -1
          ) {
            this.logoData.linkUrl =
              this.urlPrefix +
              (imageData.hrefOverride.indexOf("/") == 0
                ? imageData.hrefOverride.substr(1)
                : imageData.hrefOverride);
          } else {
            this.logoData.linkUrl = imageData.hrefOverride;
          }
          //Force the DOM to re-draw if the hrefOverride wasn't there (making the img hidden)
          // if (!this.hrefOverride) {
          //   this.hrefOverride = this.logoData.linkUrl;
          //   this.hrefOverride = "";
          // }
        }
        this.logoData.showDistributorImage = true;
        this.logoData.distributorImage = imageData.url;
      }
    },

    handleHideHeaderMenu(hide) {
      this.hideMenuEvent = !!hide;
    },

    handleSetHeaderLogoHref(logoData) {
      if (logoData && logoData.linkUrl && logoData.linkUrl.length > 0) {
        if (
          logoData.aemSource &&
          logoData.linkUrl.indexOf(this.urlPrefix) == -1
        ) {
          this.logoData.linkUrl =
            this.urlPrefix +
            (logoData.linkUrl.indexOf("/") == 0
              ? logoData.linkUrl.substr(1)
              : logoData.linkUrl);
        } else {
          this.logoData.linkUrl = logoData.linkUrl;
        }
      } // else {
      //   this.logoData.linkUrl = "";
      // }
    },

    handleSetHeaderHrefOverride(overrideData) {
      if (overrideData && overrideData.href && overrideData.href.length > 0) {
        if (
          overrideData.aemSource &&
          overrideData.href.indexOf(this.urlPrefix) == -1
        ) {
          this.logoData.linkUrl =
            this.urlPrefix +
            (overrideData.href.indexOf("/") == 0
              ? overrideData.href.substr(1)
              : overrideData.href);
        } else {
          this.logoData.linkUrl = overrideData.href;
        }
      } // else {
      //   this.logoData.linkUrl = "";
      // }
    },

    // short alias for fromJsonString
    j(s, d) {
      return fromJsonString(s, d);
    },

    removeHTTPSPub(navigation) {
      if (navigation) {
        let topnav = navigation;
        for (let navItem of navigation) {
          for (const [key, value] of Object.entries(navItem)) {
            if (this.wildcardMatch(value, "https://*-pubaws*")) {
              let newval = value;
              if (!this.wildcardMatch(value, "*nuskin.io:*")) {
                newval = newval.replace("nuskin.io", "nuskin.io:4503");
              }
              topnav[key] = newval.replace("https:", "http:");
            }
          }
        }
        return topnav;
      } else {
        return navigation;
      }
    },

    wildcardMatch(str, rule) {
      const escapeRegex = str => str.replace(/([.*+?^=!:${}()|\]\\])/g, "\\$1");
      return new RegExp(
        "^" +
          rule
            .split("*")
            .map(escapeRegex)
            .join(".*") +
          "$"
      ).test(str);
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

$headerPadding: 15px;
$headerBoxShadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);

.ns-header {
  position: relative;
  width: 100%;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }
}

/*LAYOUT*/
header.header-container {
  position: relative;
  width: 100%;
  margin: 0 auto;
  background-color: white;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  user-select: none;
  z-index: map.get(other.$otherUtils, z-header);
  box-shadow: $headerBoxShadow;
}

.header-search-results {
  display: none;
  position: relative;
  width: 100%;
  flex: 1;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  background-color: map.get(colors.$themeColors, color-background-1);
  overflow-y: auto;
}

.ns-header.show-search-results {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 100000;

  .header-search-results {
    display: block;
  }
}

.header-body {
  position: relative;
  padding: 10px;
  background-color: white;
  z-index: map.get(other.$otherUtils, z-header);
}

.header-layout {
  display: grid;
  grid-template-areas: "left center right";
  grid-gap: calc(#{$headerPadding} - 5px); // 10px
  grid-template-columns: auto 1fr auto;
}

.header-max-width {
  max-width: 100%;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.header-center {
  grid-area: center;
  display: grid;
  grid-template-areas: "centerTop";
  grid-row-gap: 10px;
  grid-template-columns: 1fr;
  align-items: center;
}

.header-left {
  grid-area: left;
  display: grid;
  grid-template-areas: "leftOne leftTwo";
  align-items: center;
  grid-column-gap: $headerPadding;
}

.header-left ::slotted(*) {
  justify-self: flex-start;
}

.header-right {
  grid-area: right;
  display: grid;
  grid-template-areas: "rightTwo";
  grid-column-gap: $headerPadding;
  align-items: center;
}

.header-right-one {
  display: none;
  grid-area: rightOne;
  align-items: center;
  height: 30px;
  & a {
    margin: 0 10px;
    color: rgb(99, 102, 106);
    font-size: 14px;
    &:hover {
      color: #000;
    }
  }
}

.header-right-two {
  grid-area: rightTwo;
  & .ns-mini-cart {
    display: block;
    margin: 0 10px;
  }
}

.header-left-one {
  grid-area: leftOne;
}

.header-left-two {
  grid-area: leftTwo;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
}

.header-center-top {
  grid-area: centerTop;
}

.header-center-bottom {
  grid-area: centerBottom;
}
/*END LAYOUT*/

/*LOGO*/
.mec-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 1px solid #272525;
}
/* line 131, ../scss/_main.scss */
.mec-avatar.is-square {
  border-radius: 0;
  width: auto;
  border: none;
}
@media (max-width: 1023px) {
  /* line 125, ../scss/_main.scss */
  .mec-avatar {
    margin-left: 5px;
  }
}
@media (max-width: 767px) {
  /* line 125, ../scss/_main.scss */
  .mec-avatar {
    margin-left: 10px;
  }
}

/*HAMBURGER MENU ANIMATION*/
.menu-button {
  position: relative;
  display: block;
  padding: 10px;
  width: 40px;
  height: 40px;
  color: map.get(colors.$themeColors, primary-text-color);
  cursor: pointer;
}

/*END HAMBURGER MENU ANIMATION*/

.ns-searchbar {
  flex-grow: 1;
}

.profile-new-customer a,
.profile-new-customer ::slotted(a) {
  text-decoration: none !important;
  position: relative;
  color: map.get(colors.$themeColors, color-expressive-6);
}

.profile-new-customer a::after,
.profile-new-customer ::slotted(a::after) {
  content: "";
  width: 100%;
  height: 1px;
  background-color: map.get(colors.$themeColors, color-expressive-6);
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  margin: auto;
  transition: map.get(other.$otherUtils, global-transition);
  opacity: 0;
  visibility: hidden;
}

.profile-new-customer a:hover::after,
.profile-new-customer ::slotted(a:hover::after) {
  opacity: 1;
  visibility: visible;
}

/* TOP BAR */
.utility-bar-area {
  background-color: rgb(246, 247, 247);
  border: none;
  min-height: 45px;
}

.utility-bar-body {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  align-items: center;
  min-height: 45px;
}

.utility-bar-area .top-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: inherit;
}

.utility-bar-area .top-center {
  overflow: hidden;
}

.textblock {
  overflow: hidden;
  position: relative;
  line-height: 1.2em;
  max-height: 2.4em;
  padding-right: 40px;
  padding-left: 1em;
}

.textblock::before {
  content: "";
  position: absolute;
  right: 20px;
  bottom: 0;
}

.mobile-full-link {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 40px);
  height: 100%;
}

.join-link {
  text-decoration: none;
  border-bottom: 1px solid transparent;
  &:hover {
    border-bottom: 1px solid currentColor;
  }
}

// NS PROFILE
.utility-bar-area .ns-profile {
  margin: 0 !important;
  margin-left: 15px !important;
  padding: 0 !important;
}

.profile__close {
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(0, 0, 0, 0.15);
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.profile__close-icon {
  width: 14px;
  height: 14px;
  color: var(--color-interactive-7);
}

@media (min-width: 1024px) {
  .utility-bar-area {
    padding: 0 15px;
    border-bottom: 1px solid rgb(236, 237, 237);
    min-height: 30px;
  }

  .utility-bar-body {
    grid-template-columns: 1fr auto 1fr;
    gap: 30px;
    min-height: 30px;

    &.unlock-center {
      display: flex;
      justify-content: space-between;
      // some browsers support flex gap, but for now we'll do a work-around for better consistancy
      gap: 0px;

      // css magic to add spacing between flex items and to account for empty items
      > :not(:first-child):not(:empty) {
        margin-left: 30px;
      }

      > :empty + :not(:empty) {
        margin-left: 0px;
      }

      .top-left:empty {
        display: none;
      }
    }
  }

  .rtl .utility-bar-area ::slotted(.ns-profile),
  .rtl .utility-bar-area .ns-profile {
    margin-left: auto;
    margin-right: 20px;
    padding-left: 0;
    padding-right: 20px;
  }

  .utility-bar-area .ns-profile {
    margin-left: 15px !important;
    padding: 0 !important;
  }

  .header-body {
    padding: 8px 15px;
  }

  .header-layout {
    grid-gap: 55px;
  }

  .header-center {
    grid-template-areas:
      "centerTop"
      "centerBottom";
  }

  .header-left,
  .header-right {
    align-items: flex-start;
  }

  .header-right {
    grid-template-areas: "rightOne rightTwo";
    justify-content: flex-end;
  }

  .header-right-one {
    display: flex;
  }

  .header-right-two {
    & .ns-mini-cart {
      margin: -4px 0 0;
    }
  }

  .header-left {
    grid-template-areas: "leftTwo";
  }

  .header-left-one {
    display: none;
  }

  .header-left .menu-button {
    display: none;
  }

  .mobile-full-link {
    display: none;
  }
}

@media (min-width: 1440px) {
  .header-max-width {
    width: 1410px;
  }
}

a[href=".html"] {
  display: none;
}
</style>
